/* Everything, by module.
 *
 * The curated menu is what people use; this is the promise behind it. Every
 * DocType in the in-scope surface is reachable from here, including ones a
 * tenant's admin added after this app was built — the index is generated from
 * a live site, so a new form appears in it rather than being invisible.
 */
import { useMemo, useState } from 'react'
import type { IndexEntry } from './nav'
import { labelFor } from './nav'
import index from './doctypes.generated.json'

const ALL = index as IndexEntry[]

export function Browse({ onOpen }: { onOpen: (doctype: string) => void }) {
  const [text, setText] = useState('')
  const [showAdmin, setShowAdmin] = useState(false)

  const groups = useMemo(() => {
    const needle = text.trim().toLowerCase()
    const by = new Map<string, IndexEntry[]>()
    for (const e of ALL) {
      if (!showAdmin && e.admin) continue
      if (needle && !e.n.toLowerCase().includes(needle) && !labelFor(e.n).toLowerCase().includes(needle)) continue
      const list = by.get(e.m) ?? []
      list.push(e)
      by.set(e.m, list)
    }
    return [...by.entries()].sort((a, b) => b[1].length - a[1].length)
  }, [text, showAdmin])

  const total = groups.reduce((n, [, list]) => n + list.length, 0)

  return (
    <div className="page" style={{ display: 'grid', gap: 16 }}>
      <div>
        <h1>Browse everything</h1>
        <p className="muted small" style={{ marginTop: 4, maxWidth: '60ch' }}>
          Every form in your ERP, grouped the way ERPNext groups them. The menu on the left is the
          short version; this is all of it.
        </p>
      </div>

      <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
        <input className="input" style={{ maxWidth: 300 }} value={text} placeholder="Find a form…"
               onChange={(e) => setText(e.target.value)} aria-label="Find a form" />
        <label className="check">
          <input type="checkbox" checked={showAdmin} onChange={(e) => setShowAdmin(e.target.checked)} />
          <span className="small">Include settings and admin forms</span>
        </label>
        <span className="grow" />
        <span className="small muted">{total} forms</span>
      </div>

      <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
        {groups.map(([module, entries]) => (
          <section key={module} className="card card-pad">
            <div className="row-between" style={{ marginBottom: 8 }}>
              <h3>{module}</h3>
              <span className="chip">{entries.length}</span>
            </div>
            <div className="stack" style={{ gap: 1 }}>
              {entries.map((e) => (
                <button key={e.n} className="navitem" onClick={() => onOpen(e.n)}>
                  <span className="grow truncate">{labelFor(e.n)}</span>
                  {e.s ? <span className="chip chip-info" title="Documents here are submitted">submits</span> : null}
                  {e.g ? <span className="chip" title="A single settings record">settings</span> : null}
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
      {total === 0 ? <p className="muted">Nothing matches “{text}”.</p> : null}
    </div>
  )
}
